/* eslint consistent-return: "off" */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import { Box, Button } from "@chakra-ui/react";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import { ReactComponent as RefreshIcon } from "assets/icons/table-refresh-icon.svg";
import { useBusinesses } from "contexts/businesses-context";
import { ExportDataEnum, useExportData } from "hooks/useExportData";
import { useFilterQueryParams } from "hooks/useFilterQueryParams";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { debounce } from "lodash";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ROUTES } from "routes/constants";
import { haveQueryParamsChanged } from "utils";
import {
  NO_NEW_FILTER_MESSAGE,
  SEARCH_DEBOUNCE_TIMER,
  START_INDEX,
  ZERO,
} from "utils/constants";

import { Button as EpButton } from "ui/button";
import { customWarningToast } from "ui/custom-toast";
import { Export } from "ui/export";
import { TExportType } from "ui/export/types";
import { Filter } from "ui/filter";
import {
  IDateRangeValues,
  IFilterRef,
  TFilterOption,
  TFilterValue,
  TFilterValues,
} from "ui/filter/types";
import { tableHeaderCtaCss } from "ui/table/styles";
import { TableSearchInput } from "ui/table/table-search-input";

import { BusinessesStatus } from "../content";
import { BUSINESSES_LIST_TABLE_PAGINATION_PARAMS } from "./const";
import { ITableHeaderCTAsProps } from "./types";

const LG_WIDTH = 900;
const DEBOUNCE_TIMER = 50;
const AVAILABLE_EXPORT_OPTIONS: TExportType[] = ["csv", "pdf"];

const ALL_COMPLIANCE_STATUSES = [
  "all",
  "approved",
  "rejected",
  "review",
  "pending",
  /*   "started",
  "not-started" */
];

const ALL_BUSINESS_TYPES = ["all", "registered", "unregistered"];

export const TableHeaderCTAs = memo((props: ITableHeaderCTAsProps) => {
  const { isLoading, isRefreshing, handleRefreshTable } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { activeStatus } = useParams<{ activeStatus: BusinessesStatus }>();
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const businessType = searchParams.get("businessType");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const orderBy = searchParams.get("orderBy");
  const search = searchParams.get("search");
  const updateQueryParams = useUpdateQueryParams();
  const { isFilterError, setIsFilterError, filterRetryCountParams } =
    useFilterQueryParams({
      apiFilterRoute: "/control/business/compliance/businesses",
    });
  const [searchQuery, setSearchQuery] = useState(search || "");
  const filterRef = useRef<IFilterRef | null>(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filterValues, setFilterValues] = useState<TFilterValues>({
    status: status ? status.split(",") : [],
    businessType: businessType ? businessType.split(",") : [],
    balance: orderBy || "",
    activeStatus: [],
    dateRange: {
      startDate,
      endDate,
    },
  });
  const {
    isExporting,
    handleExport,
    handleResetExport,
    handleExportTypeChange,
    handleDateRangeChange,
    dateRange,
    exportType,
    dropDownRef,
  } = useExportData({
    type: ExportDataEnum.EMPLOYER,
  });

  const isFilterDisabled = !(
    (filterValues?.status as string[])?.length > ZERO ||
    (filterValues?.businessType as string[])?.length > ZERO ||
    (filterValues?.dateRange as IDateRangeValues)?.startDate ||
    filterValues?.balance
  );

  const disableAllFiltersExceptBalanceFilter = !!filterValues.balance;
  const filterOptions: TFilterOption[] = [
    {
      type: "checkbox",
      title: "Compliance Status",
      key: "status",
      isDisabled: disableAllFiltersExceptBalanceFilter,
      options: [
        {
          label: "All",
          value: "all",
        },
        {
          label: "Approved",
          value: "approved",
        },
        {
          label: "Rejected",
          value: "rejected",
        },
        {
          label: "Ready for Review",
          value: "review",
        },
        {
          label: "Pending",
          value: "pending",
        },
        /* {
          label: "Started",
          value: "started",
        },
        {
          label: "Not Started",
          value: "not-started",
        }, */
      ],
    },
    {
      type: "radio",
      title: "Balance",
      key: "balance",
      isDisabled: activeStatus && activeStatus !== BusinessesStatus.ALL,
      options: [
        {
          label: "Highest to Lowest",
          value: "desc",
        },
        {
          label: "Lowest to Highest",
          value: "asc",
        },
      ],
    },
    {
      type: "checkbox",
      title: "Business Type",
      key: "businessType",
      isDisabled: disableAllFiltersExceptBalanceFilter,
      options: [
        {
          label: "All",
          value: "all",
        },
        {
          label: "Registered",
          value: "registered",
        },
        {
          label: "Unregistered",
          value: "unregistered",
        },
      ],
    },
    {
      type: "date-range",
      title: "Custom Date Range",
      key: "dateRange",
      isDisabled: disableAllFiltersExceptBalanceFilter,
    },
  ];

  const handleResetFilter = () => {
    setFilterValues({
      status: [],
      businessType: [],
      balance: "",
      activeStatus: [],
      dateRange: {
        startDate: null,
        endDate: null,
      },
    });
  };

  const onBusinessListSearch = useCallback(
    debounce(async (value: string) => {
      if (value.trim() === "") {
        updateQueryParams({
          ...Object.fromEntries(new URLSearchParams(location.search)),
          search: null,
          page: String(BUSINESSES_LIST_TABLE_PAGINATION_PARAMS.page),
          limit: String(BUSINESSES_LIST_TABLE_PAGINATION_PARAMS.limit),
          isDateFilter: "false",
          orderBy: null,
        });
        return;
      }

      updateQueryParams({
        ...Object.fromEntries(new URLSearchParams(location.search)),
        search: value,
        page: String(BUSINESSES_LIST_TABLE_PAGINATION_PARAMS.page),
        limit: String(BUSINESSES_LIST_TABLE_PAGINATION_PARAMS.limit),
        isDateFilter: "false",
        orderBy: null,
      });
      /*companyData.businessName,companyData.businessName */
    }, SEARCH_DEBOUNCE_TIMER),
    [location]
  );

  const handleSearchQueryChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = evt.target;
    setSearchQuery(value);
    onBusinessListSearch(value);
  };

  const handleFilterValueChange = (key: string, value: TFilterValue) => {
    let newValue = value;
    if (key === "status") {
      const complianceStatuses = value as string[];
      const currentBusinessComplianceStatus = filterValues?.status as string[];
      if (
        complianceStatuses.includes("all") &&
        currentBusinessComplianceStatus.length < ALL_COMPLIANCE_STATUSES.length
      ) {
        newValue = ALL_COMPLIANCE_STATUSES;
      } else {
        newValue = complianceStatuses.filter(
          (complianceStatus) => complianceStatus !== "all"
        );
      }
    } else if (key === "businessType") {
      const businessTypes = value as string[];
      const currentBusinessTypes = filterValues?.businessType as string[];
      if (
        businessTypes.includes("all") &&
        currentBusinessTypes.length < ALL_BUSINESS_TYPES.length
      ) {
        newValue = ALL_BUSINESS_TYPES;
      } else {
        newValue = businessTypes.filter(
          (businessType) => businessType !== "all"
        );
      }
    }
    setFilterValues((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const handleApplyFilter = async () => {
    const newQueryParams: Record<string, string | null> = {};
    const startDate = (filterValues.dateRange as IDateRangeValues).startDate;
    const endDate = (filterValues.dateRange as IDateRangeValues).endDate;
    const status = filterValues.status;
    const balance = filterValues.balance;
    const businessType = filterValues.businessType;

    if (startDate) {
      newQueryParams["startDate"] = startDate;
    } else {
      newQueryParams["startDate"] = null;
    }

    if (endDate) {
      newQueryParams["endDate"] = endDate;
    } else {
      newQueryParams["endDate"] = null;
    }

    if (Array.isArray(status) && status.length > ZERO) {
      newQueryParams["status"] = status.join(",");
    } else {
      newQueryParams["status"] = null;
    }

    if (Array.isArray(businessType) && businessType.length > ZERO) {
      newQueryParams["businessType"] = businessType.join(",");
    } else {
      newQueryParams["businessType"] = null;
    }

    if (balance) {
      newQueryParams["orderBy"] = balance as string;
    } else {
      newQueryParams["orderBy"] = null;
    }

    const paginationQueryParams = {
      page: BUSINESSES_LIST_TABLE_PAGINATION_PARAMS.page.toString(),
      limit: BUSINESSES_LIST_TABLE_PAGINATION_PARAMS.limit.toString(),
    };
    setIsFilterError(false);
    if (balance) {
      updateQueryParams({
        orderBy: balance as string,
        ...paginationQueryParams,
        ...filterRetryCountParams,
      });
    } else {
      updateQueryParams({
        ...Object.fromEntries(new URLSearchParams(location.search)),
        ...newQueryParams,
        isDateFilter: "false",
        ...paginationQueryParams,
        ...filterRetryCountParams,
      });
    }

    if (
      haveQueryParamsChanged({
        currentSearch: location.search,
        newParams: newQueryParams,
      }) ||
      isFilterError
    ) {
      setIsFiltering(true);
    } else {
      customWarningToast({
        message: NO_NEW_FILTER_MESSAGE,
      });
    }
  };

  const handleNavigateToAddNewBusinessPage = () => {
    navigate(ROUTES.ADD_BUSINESSES_PAGE);
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const {
    businessesTableHeaderCTAWidth,
    handleUpdateBusinessesTableHeaderCTAWidth,
  } = useBusinesses();

  const debouncedUpdateWidth = debounce(() => {
    if (wrapperRef.current && wrapperRef.current.offsetWidth !== START_INDEX) {
      const newWidth = wrapperRef.current.offsetWidth;
      if (
        (newWidth <= LG_WIDTH && businessesTableHeaderCTAWidth <= LG_WIDTH) ||
        (newWidth > LG_WIDTH && businessesTableHeaderCTAWidth > LG_WIDTH)
      ) {
        return;
      }
      const newElementWidth = wrapperRef.current.offsetWidth;
      handleUpdateBusinessesTableHeaderCTAWidth(newElementWidth);
    }
  }, DEBOUNCE_TIMER);

  useEffect(() => {
    if (disableAllFiltersExceptBalanceFilter) {
      setFilterValues({
        ...filterValues,
        status: [],
        businessType: [],
        activeStatus: [],
        dateRange: {
          startDate: null,
          endDate: null,
        },
      });
    }
  }, [disableAllFiltersExceptBalanceFilter]);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new ResizeObserver(() => {
      debouncedUpdateWidth();
    });
    observer.observe(wrapperRef.current);

    return () => {
      observer.disconnect();
    };
  }, [businessesTableHeaderCTAWidth]);

  useEffect(() => {
    if (!isLoading && filterRef?.current?.isFilterOpen) {
      setIsFiltering(false);
      filterRef?.current?.handleToggleFilter();
    }
  }, [isLoading, filterRef]);

  useEffect(() => {
    if (!isLoading && isRefreshing) {
      handleResetFilter();
    }
  }, [isLoading]);

  return (
    <Box
      className={`flex px-3 ${businessesTableHeaderCTAWidth < LG_WIDTH ? "flex-col h-[130px] justify-evenly items-start" : "items-center justify-between h-[85px]"}`}
      ref={wrapperRef}
    >
      <Box className="flex items-center gap-4">
        <TableSearchInput
          searchQuery={searchQuery}
          handleSearchQueryChange={handleSearchQueryChange}
        />
        <Filter
          options={filterOptions}
          values={filterValues}
          onChange={handleFilterValueChange}
          onApplyFilter={handleApplyFilter}
          filterBtnCss={tableHeaderCtaCss}
          isLoading={isFiltering}
          ref={filterRef}
          isApplyBtnDisabled={isFilterDisabled}
          handleResetFilter={handleResetFilter}
        />
        <Export
          isLoading={isExporting}
          handleExport={handleExport}
          dateRange={dateRange}
          exportType={exportType}
          handleExportTypeChange={handleExportTypeChange}
          handleDateRangeChange={handleDateRangeChange}
          dropDownRef={dropDownRef}
          availableExportOptions={AVAILABLE_EXPORT_OPTIONS}
          handleResetExport={handleResetExport}
        />
        <Button
          css={tableHeaderCtaCss}
          onClick={!isRefreshing ? handleRefreshTable : undefined}
          disabled={isRefreshing}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
          <p className="text-sm text-[#374151] font-semibold">Refresh</p>
        </Button>
      </Box>
      <EpButton
        variant="solid"
        icon={<PlusIcon />}
        css={{
          height: "37px",
          borderRadius: "8px",
        }}
        onClick={handleNavigateToAddNewBusinessPage}
      >
        Add New Business
      </EpButton>
    </Box>
  );
});
